import { Contracts } from './contracts.enum';
import { Job, JobRole } from './job.model';
import { WDFValue } from './wdf.model';

export interface Worker {
  trainingAlert?: number;
  uid?: string;
  ustatus?: string;
  nameOrId: string;
  contract: Contracts;
  mainJob: JobRole;
  localIdentifier: string;
  approvedMentalHealthWorker?: string;
  otherJobs?: {
    value: string;
    jobs: JobRole[];
  };
  mainJobStartDate?: string;
  nationalInsuranceNumber?: string;
  dateOfBirth?: string;
  postcode?: string;
  gender?: string;
  disability?: string;
  ethnicity?: {
    ethnicityId: number;
    ethnicity?: string;
  };
  nationality?: {
    value: string;
    other?: {
      nationalityId?: number;
      nationality?: string;
    };
  };
  recruitedFrom?: {
    value: string;
    from?: {
      recruitedFromId: number;
      from?: string;
    };
  };
  britishCitizenship?: string;
  countryOfBirth?: {
    value: string;
    other?: {
      countryId?: number;
      country?: string;
    };
  };
  yearArrived?: {
    value: string;
    year: number;
  };
  socialCareStartDate?: {
    value: string;
    year: number;
  };
  carryOutDelegatedHealthcareActivities?: string;
  daysSick?: WorkerDays;
  zeroHoursContract: string;
  weeklyHoursAverage: {
    value: string;
    hours: number;
  };
  weeklyHoursContracted: {
    value: string;
    hours: number;
  };
  annualHourlyPay: WorkerPay;
  careCertificate: string;
  level2CareCertificate?: {
    value: string;
    year?: number;
  };
  apprenticeshipTraining: string;
  qualificationInSocialCare: string;
  socialCareQualification: {
    qualificationId: number;
    title?: string;
  };
  otherQualification: string;
  highestQualification: {
    qualificationId: number;
    title?: string;
  };
  registeredNurse: string;
  created?: string;
  updated?: string;
  completed?: boolean;
  wdf?: WorkerWdfRecord;
  wdfEligible: boolean;
  jobRole?: string;
  trainingCount: number;
  trainingLastUpdated?: string;
  qualificationsLastUpdated?: string;
  expiredTrainingCount: number;
  expiringTrainingCount: number;
  missingMandatoryTrainingCount: number;
  qualificationCount: number;
  longTermAbsence: string;
  healthAndCareVisa?: string;
  employedFromOutsideUk?: string;
  careWorkforcePathwayRoleCategory?: {
    roleCategoryId: number;
    title: string;
    description: string;
  };
}

const MandatoryInfoFields = ['nameOrId', 'mainJob', 'contract'];
const MetadataFields = [
  'uid',
  'ustatus',
  'created',
  'updated',
  'updatedBy',
  'completed',
  'localIdentifier',
  'wdf',
  'wdfEligible',
  'trainingCount',
  'trainingLastUpdated',
  'expiredTrainingCount',
  'qualificationsLastUpdated',
  'missingMandatoryTrainingCount',
  'qualificationCount',
];

export const MandatoryInfoAndMetadataFields = MandatoryInfoFields.concat(MetadataFields);

export interface WorkerPay {
  value: string;
  rate: number;
}

export interface WorkerDays {
  value: number;
  days?: number;
}

export interface WorkerWdfRecord {
  annualHourlyPay: WDFValue;
  careCertificate: WDFValue;
  contract: WDFValue;
  currentEligibility: boolean;
  dateOfBirth: WDFValue;
  daysSick: WDFValue;
  effectiveFrom: string;
  gender: WDFValue;
  highestQualification: WDFValue;
  isEligible: boolean;
  lastEligibility: string;
  mainJob: WDFValue;
  mainJobStartDate: WDFValue;
  nationality: WDFValue;
  otherQualification: WDFValue;
  qualificationInSocialCare: WDFValue;
  recruitedFrom: WDFValue;
  socialCareQualification: WDFValue;
  weeklyHoursAverage: WDFValue;
  weeklyHoursContracted: WDFValue;
  zeroHoursContract: WDFValue;
}

export type WorkersGroupedByJobRole = {
  title: string;
  jobId: number;
  workers: Array<{ uid: string }>;
  count: number;
  annualHourlyPay?: WorkerPay;
};

export interface WorkersGroupedByJobRoleResponse {
  groups: Array<WorkersGroupedByJobRole>;
}

export type WorkersGroupedByJobRoleWithIndex = WorkersGroupedByJobRole & {
  originalIndex: number;
};

export interface WorkersResponse {
  workers: Worker[];
  workerCount: number;
}

export type WorkerWithPayData = {
  uid: string;
  nameOrId: string;
  mainJob: Job;
  annualHourlyPay: WorkerPay;
};

export interface WorkersWithPayDataResponse {
  workers: WorkerWithPayData[];
  count: number;
}

export interface WorkerEditResponse {
  uid: string;
}

export enum SelectRecordTypes {
  Training = 'Training course',
  Qualification = 'Qualification',
}
